<i18n src="@/i18n/components/user-name-form.json"></i18n>

<template>
  <div class="level-left" v-if="!isEditable">
    <div class="level-item">
      <p class="title is-4 c-username c-username--truncated" :title="user.name">{{ user.name }}</p>
    </div>
  </div>

  <div class="level-left" v-else>
    <div class="level-item">
      <div v-if="!displayUserNameInput" class="level">
        <div class="level-left">
          <div class="level-item">
            <p class="title is-4 c-username c-username--truncated" :title="user.name">{{ user.name }}</p>
          </div>

          <div class="level-item">
            <div class="field has-addons">
              <p class="control">
                <button class="button" @click="editUser" :disabled="isDemoUser()">
                  <span class="icon is-small"><i class="fa fa-pencil"></i></span>
                </button>
              </p>
              <p class="control" v-if="user.id !== event.initiator.id">
                <button class="button" @click="showUserDeleteModal = true" :class="{ 'is-loading': isLoading }" :disabled="isLoading || isDemoUser()">
                  <span class="icon is-small"><i class="fa fa-times"></i></span>
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="displayUserNameInput" class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="field">
              <div class="control">
                <input class="input is-medium" size="20" v-model="userName" :placeholder="$t('userName')" @keyup.enter="updateUserName">
              </div>
            </div>
          </div>

          <div class="level-item">
            <div class="field has-addons">
              <div class="control">
                <button class="button" @click="updateUserName" :class="{ 'is-loading': isLoading }" :disabled="userName.trim() === '' || isLoading">
                  <span class="icon is-small"><i class="fa fa-check"></i></span>
                  <span>{{ $t('validate') }}</span>
                </button>
              </div>

              <div class="control">
                <button class="button" @click="hideUserUpdate">
                  <span>{{ $t('cancel') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="user.id === event.initiator.id" class="level-item">
      <span class="tag is-info is-medium">{{ $t('host') }}</span>
    </div>

    <div class="modal" :class="{ 'is-active': showUserDeleteModal }">
      <div class="modal-background" @click="showUserDeleteModal = false"></div>
      <div class="modal-card has-text-left">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('deleteModalTitle') }}</p>
          <button class="delete" @click="showUserDeleteModal = false"></button>
        </header>
        <section class="modal-card-body">
          {{ $t('deleteModalText', {userName: user.name}) }}
        </section>
        <footer class="modal-card-foot">
          <div class="buttons">
            <button class="button is-success" @click="showUserDeleteModal = false">{{ $t('deleteModalBackButton') }}</button>
            <button class="button is-danger" @click="deleteUser" :class="{ 'is-loading': isLoading }" :disabled="isLoading">
              <span class="icon is-small"><i class="fa fa-exclamation-triangle"></i></span>
              <span>{{ $t('deleteModalDeleteButton') }}</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
  import gql from 'graphql-tag'
  import { getEventById } from '@/shared/queries'
  import { bus } from '@/shared/bus'

  const updateUser = gql`
    mutation updateUser($userId: ID!, $name: String!) {
      updateUser(id: $userId, name: $name) {
        id
        name
      }
    }
  `

  export default {
    name: 'user-name-form',
    props: {
      user: {
        type: Object,
        required: true
      },
      event: {
        type: Object,
        required: true
      },
      isEditable: {
        type: Boolean,
        required: false,
        default: true
      }
    },
    data () {
      return {
        displayUserNameInput: false,
        userName: this.user.name,
        isLoading: false,
        showUserDeleteModal: false
      }
    },
    watch: {
      user (newValue) {
        this.userName = newValue.name
      }
    },
    methods: {
      editUser () {
        this.displayUserNameInput = true
      },
      hideUserUpdate () {
        this.displayUserNameInput = false
      },
      updateUserName () {
        this.isLoading = true

        this.$apollo.mutate({
          mutation: updateUser,
          variables: {
            userId: this.user.id,
            name: this.userName
          }
        }).then(() => {
          this.$toast.open({
            message: this.$t('userUpdated'),
            position: 'is-bottom',
            type: 'is-success'
          })

          this.displayUserNameInput = false
        }).catch((error) => {
          // Error
          bus.$emit('toast:new', {type: 'danger'})

          this.$ga && this.$ga.exception(error.message || error)
        }).then(() => {
          this.isLoading = false
        })
      },
      deleteUser () {
        this.isLoading = true

        // Delete
        let itemsToDeleteIds = []
        for (let item of this.user.items) {
          itemsToDeleteIds.push(item.id)
        }
        let deleteQueries = ''
        if (itemsToDeleteIds.length > 0) {
          for (let id of itemsToDeleteIds) {
            deleteQueries += id + ': deleteItem(id: "' + id + '") {id}'
          }
        }

        let deleteUser = gql`
          mutation deleteUser($userId: ID!) {
            deleteUser(id: $userId) {
              id
            }
            ${deleteQueries}
          }
        `

        this.$apollo.mutate({
          mutation: deleteUser,
          variables: {
            userId: this.user.id
          },
          refetchQueries: [{
            query: getEventById,
            variables: {
              id: this.event.id
            }
          }]
        }).then(() => {
          // Result
          this.showUserDeleteModal = false
          this.$toast.open({
            message: this.$t('deleteSuccess'),
            position: 'is-bottom',
            type: 'is-success'
          })
        }).catch((error) => {
          // Error
          this.$ga && this.$ga.exception(error.message || error)
          bus.$emit('toast:new', {type: 'danger'})
        }).then(() => {
          this.isLoading = false
        })
      },
      isDemoUser () {
        if (this.event.id === process.env.DEMO_EVENT_ID) {
          if (process.env.DEMO_USERS_IDS.split(' ').includes(this.user.id)) {
            return true
          }
        }

        return false
      }
    }
  }
</script>

<style scoped>
  .c-username--truncated {
    max-width: 320px;
    /*white-space: nowrap;*/
    overflow: hidden;
    line-height: 1.5;
    text-overflow: ellipsis;
  }
</style>



// WEBPACK FOOTER //
// src/components/UserNameForm.vue